import { Provider as BalancerProvider } from 'react-wrap-balancer';

import { ApolloProvider } from '@apollo/client';
// import client from '@app/client.ts';
import { ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { ReactNode, useEffect, useState } from 'react';
import Values from 'values.js';
import { useApolloClient } from './context/ApolloClientContext';
import { FlowProvider } from './context/FlowContext';
import { MessageProvider } from './context/MessageContext';
import { MessageQueueProvider } from './context/MessageQueueContext';
import { NavigationProvider } from './context/NavigationContext';
import { OnboardingStateMachineProvider } from './context/OnboardingStateMachineContext';
// import { OrderProvider } from './context/OrderContext';
import { TaskProvider } from './context/TaskContext';
import { UserProvider } from './context/UserContext';
import { useUserWithCompany } from './hooks/useUserWithCompany';

export const makeTheme = (
  primaryColor: string,
  secondaryColor: string,
  successColor?: string,
  warningColor?: string,
  dangerColor?: string
) =>
  createTheme({
    palette: {
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: secondaryColor,
      },
      success: { main: successColor || '#91ab46' },
      warning: { main: warningColor || '#ffc107' },
      error: { main: dangerColor || '#ec474f' },
    },
    typography: {
      fontFamily: ['DieterRubik'].join(','),
    },
    zIndex: {
      snackbar: 10000,
    },
  });

interface IProps {
  children: ReactNode;
}

function AppProvider({ children }: IProps) {
  const { client } = useApolloClient();
  if (!client) return null;
  return (
    <ApolloProvider client={client}>
      <SnackbarProvider maxSnack={5} classes={{ containerRoot: 'z-alert' }}>
        <UserProvider>
          <ThemeWrapper>
            <NavigationProvider>
              <OnboardingStateMachineProvider>
                <MessageQueueProvider>
                  <MessageProvider>
                    <TaskProvider>
                      <FlowProvider>
                        <BalancerProvider>{children}</BalancerProvider>
                      </FlowProvider>
                    </TaskProvider>
                  </MessageProvider>
                </MessageQueueProvider>
              </OnboardingStateMachineProvider>
            </NavigationProvider>
          </ThemeWrapper>
        </UserProvider>
      </SnackbarProvider>
    </ApolloProvider>
  );
}

// ThemeWrapper wraps around MUIs ThemeProvider to add dynamic color capabilities.
function ThemeWrapper({ children }: IProps) {
  const { whitelabelConfig, whitelabelLoading: loading } = useUserWithCompany();

  const [theme, setTheme] = useState(makeTheme('#ab5fce', '#fdd213'));

  useEffect(() => {
    applyDieterColors();
  }, []);

  useEffect(() => {
    if (loading) return;

    // If we have whitelabelConfig (from token, company, or global), use it
    if (whitelabelConfig) {
      const primaryColor = whitelabelConfig.primaryColor;
      const secondaryColor = whitelabelConfig.secondaryColor;
      setTheme(
        makeTheme(
          primaryColor,
          secondaryColor,
          whitelabelConfig.successColor,
          whitelabelConfig.warningColor,
          whitelabelConfig.dangerColor
        )
      );
      applyCustomTheme(
        primaryColor,
        secondaryColor,
        whitelabelConfig.successColor,
        whitelabelConfig.warningColor,
        whitelabelConfig.dangerColor
      );
      return;
    }

    // Otherwise, apply default Dieter colors
    applyDieterColors();
  }, [loading, whitelabelConfig]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default AppProvider;

export const applyCustomTheme = (
  primaryColor: string,
  secondaryColor: string,
  successColor?: string,
  warningColor?: string,
  dangerColor?: string
) => {
  const tintSteps = ['50', '100', '200', '300', '400', '500'];
  const shadeSteps = ['600', '700', '800', '900'];

  // Primary color
  let contrast = '18';
  for (let i = 0; i < tintSteps.length; i++) {
    const color = '#' + new Values(primaryColor).tint((tintSteps.length - i) * parseInt(contrast)).hex;
    document.documentElement.style.setProperty(`--primary-${tintSteps[i]}`, color);
  }
  document.documentElement.style.setProperty('--primary-root', primaryColor);
  for (let i = 0; i < shadeSteps.length; i++) {
    const color = '#' + new Values(primaryColor).shade((i + 1) * parseInt(contrast)).hex;
    document.documentElement.style.setProperty(`--primary-${shadeSteps[i]}`, color);
  }
  document.documentElement.style.setProperty('--primary-root-t20', primaryColor + '33');

  // Secondary color
  contrast = '12';
  for (let i = 0; i < tintSteps.length; i++) {
    const color = '#' + new Values(secondaryColor).tint((tintSteps.length - i) * parseInt(contrast)).hex;
    document.documentElement.style.setProperty(`--secondary-${tintSteps[i]}`, color);
  }
  document.documentElement.style.setProperty('--secondary-root', secondaryColor);
  for (let i = 0; i < shadeSteps.length; i++) {
    const color = '#' + new Values(secondaryColor).shade((i + 1) * parseInt(contrast)).hex;
    document.documentElement.style.setProperty(`--secondary-${shadeSteps[i]}`, color);
  }
  document.documentElement.style.setProperty('--secondary-root-t20', secondaryColor + '33');

  // Generic generator for additional palettes (success, warning, danger) if provided
  const generateExtra = (name: string, base?: string, contrast = 16) => {
    if (!base) return;
    try {
      for (let i = 0; i < tintSteps.length; i++) {
        const color = '#' + new Values(base).tint((tintSteps.length - i) * parseInt(String(contrast))).hex;
        document.documentElement.style.setProperty(`--${name}-${tintSteps[i]}`, color);
      }
      document.documentElement.style.setProperty(`--${name}-root`, base);
      // base as 500 to mirror static palette structure
      document.documentElement.style.setProperty(`--${name}-500`, base);
      // 20% alpha token for convenience (mirrors primary/secondary pattern)
      document.documentElement.style.setProperty(`--${name}-root-t20`, base + '33');
      for (let i = 0; i < shadeSteps.length; i++) {
        const color = '#' + new Values(base).shade((i + 1) * parseInt(String(contrast))).hex;
        document.documentElement.style.setProperty(`--${name}-${shadeSteps[i]}`, color);
      }
    } catch (e) {
      // swallow errors if invalid color string
      console.warn(`Could not generate dynamic palette for ${name}:`, e);
    }
  };

  generateExtra('success', successColor, 14);
  generateExtra('warning', warningColor, 12);
  generateExtra('danger', dangerColor, 14);
};

export const applyDieterColors = () => {
  const setProp = (prop: string, color: string) => {
    document.documentElement.style.setProperty(prop, color);
  };
  setProp('--primary-50', '#f5ecfb');
  setProp('--primary-100', '#eddcf8');
  setProp('--primary-200', '#e0c1f1');
  setProp('--primary-300', '#cc99e7');
  setProp('--primary-400', '#b871db');
  setProp('--primary-root', '#ab5fce');
  setProp('--primary-root-t20', '#ab5fce33');
  setProp('--primary-600', '#8e40b0');
  setProp('--primary-700', '#773891');
  setProp('--primary-800', '#612f74');
  setProp('--primary-900', '#612f74');
  setProp('--secondary-50', '#fefce8');
  setProp('--secondary-100', '#fefce8');
  setProp('--secondary-200', '#fffbc2');
  setProp('--secondary-300', '#fff388');
  setProp('--secondary-400', '#ffe545');
  setProp('--secondary-root', '#fdd213');
  setProp('--secondary-root-t20', '#fdd21333');
  setProp('--secondary-600', '#edb905');
  setProp('--secondary-700', '#cd8e01');
  setProp('--secondary-800', '#a36505');
  setProp('--secondary-900', '#874f0c');

  // Success palette (static Dieter base values)
  setProp('--success-50', '#f7f8ed');
  setProp('--success-100', '#ebf0d7');
  setProp('--success-200', '#d9e3b3');
  setProp('--success-300', '#bfd086');
  setProp('--success-400', '#a6bc5f');
  setProp('--success-root', '#91ab46');
  setProp('--success-500', '#91ab46');
  setProp('--success-600', '#6a7f31');
  setProp('--success-700', '#516229');
  setProp('--success-800', '#434f25');
  setProp('--success-900', '#3a4423');
  setProp('--success-950', '#1d240f');
  setProp('--success-root-t20', '#91ab4633');

  // Warning palette
  setProp('--warning-50', '#ffffea');
  setProp('--warning-100', '#fffbc5');
  setProp('--warning-200', '#fff885');
  setProp('--warning-300', '#ffee46');
  setProp('--warning-400', '#ffdf1b');
  setProp('--warning-root', '#ffc107');
  setProp('--warning-500', '#ffc107');
  setProp('--warning-600', '#e29400');
  setProp('--warning-700', '#bb6902');
  setProp('--warning-800', '#985108');
  setProp('--warning-900', '#7c420b');
  setProp('--warning-950', '#482200');
  setProp('--warning-root-t20', '#ffc10733');

  // Danger palette
  setProp('--danger-50', '#fef2f2');
  setProp('--danger-100', '#fee6e5');
  setProp('--danger-200', '#fccfcf');
  setProp('--danger-300', '#f9a8a8');
  setProp('--danger-400', '#f57779');
  setProp('--danger-root', '#ec474f');
  setProp('--danger-500', '#ec474f');
  setProp('--danger-600', '#dc3545');
  setProp('--danger-700', '#b7192c');
  setProp('--danger-800', '#99182c');
  setProp('--danger-900', '#83182c');
  setProp('--danger-950', '#490812');
  setProp('--danger-root-t20', '#ec474f33');
};
